import moment from "moment";
import { WDQMS_API, fetchData } from "../../utils/api";
import * as actionTypes from "./actionTypes";

/**
 * Dictionary for WIS2GTS report types with their supported period types and defaults
 */
export const WIS2GTS_REPORT_TYPES = {
  wis2live: {
    periodTypes: ["hourly"],
    defaultPeriodType: "hourly",
    url: {
      hourly: "hourly-periods"
    }
  },
  "gts-to-wis2": {
    periodTypes: ["daily"],
    defaultPeriodType: "daily",
    url: {
      // six_hour: "sixhour-periods",
      daily: "daily-periods"
    }
  },
  "tac-to-bufr": {
    periodTypes: ["daily"],
    defaultPeriodType: "daily",
    url: {
      daily: "daily-periods"
    }
  },
  bufr_oscar_coords: {
    periodTypes: ["daily"],
    defaultPeriodType: "daily",
    url: {
      daily: "daily-periods"
    }
  }
};

/**
 * Sets the period data key
 * @param {Object} data - The data to set
 * @returns {Object} The action object
 */
const setPeriodDataKey = data => {
  return {
    type: actionTypes.SET_PERIOD_DATA_KEY,
    payload: data
  };
};

/**
 * Sets the property
 * @param {Object} data - The data to set
 * @returns {Object} The action object
 */
export const setProperty = data => {
  return { type: actionTypes.SET_PROPERTY, payload: data };
};

/**
 * Fetches data from the API for a specific report type and period type
 * @param {Object} payload - The payload containing the file type and period type
 * @param {string} pageType - The page type
 * @returns {Promise} A promise that resolves to the fetched data
 */
export const fetchWis2GtsDataFromApi = (payload, pageType) => {
  return dispatch => {
    let reportType = WIS2GTS_REPORT_TYPES[payload.params.report];
    // Create an array of promises for each period type
    const periodPromises = reportType.periodTypes.map(periodType => {
      return fetchData(
        `${WDQMS_API}/${reportType.url[periodType]}/?file_type=${payload.fileType}&period_type=${periodType}&report=${payload.params.report}`
      )
        .then(response => {
          // Return the response and periodType to use in the next step
          return { response, periodType };
        })
        .catch(error => {
          console.log(error);
          return { error, periodType };
        });
    });

    // Wait for all period types to be fetched before processing
    Promise.all(periodPromises).then(results => {
      // Process each result
      results.forEach(({ response, periodType, error }) => {
        if (error || !response || response.data.count === 0) {
          dispatchNoResults(payload, periodType, dispatch);
          return;
        }
        // No other params besides report
        // cause for sixhour period we have response different than hourly, daily and monthly
        let realResponse = response.data.results || response.data;

        let latestDate = realResponse[0].date;
        // latestdata is like 2025-07-16T01:00:00Z  we need date to be 2025-07-16 and latest hour to be 01
        latestDate = latestDate.split("T")[0];

        // Process the result for this period type and set periodData
        let periodData = organizePeriodAvailability(realResponse, periodType);

        // 1. Dispatch periodData it's common step for all periods
        dispatchPeriodDataRetrieved(dispatch, payload, periodType, periodData);
        // Check if No URL params are set except report
        let noUrlParams =
          Object.keys(payload.params).filter(key => key !== "report").length ===
          0;
        if (noUrlParams) {
          // 2. Dispatch selectedDate
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedDate",
              value: latestDate
            })
          );

          // 3. Dispatch selectedPeriodType to defaultPeriodType
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedPeriodType",
              value: reportType.defaultPeriodType
            })
          );

          // 4. Dispatch periodTypes
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "periodTypes",
              value: reportType.periodTypes
            })
          );

          // 5. Dispatch selectedCenter to "all"
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedCenter",
              value: "all"
            })
          );

          // 6. Dispatch selectedReport to report
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedReport",
              value: payload.params.report
            })
          );

          // 7. Dispatch selectedHourlyPeriod to latestHour
          let latestHour = getLatestHour(response);
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedHourlyPeriod",
              value: latestHour
            })
          );
        } else {
          // URL Params are set
          // 1. Dispatch selectedDate to date
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedDate",
              value: payload.params.date
            })
          );
          // 2. Dispatch selectedPeriodType to periodType
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedPeriodType",
              value: payload.params.periodType
            })
          );

          // 3. Dispatch periodTypes to reportType.periodTypes
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "periodTypes",
              value: reportType.periodTypes
            })
          );

          // 4. Dispatch selectedCenter to center
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedCenter",
              value: payload.params.center
            })
          );

          // 5. Dispatch selectedReport to report
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedReport",
              value: payload.params.report
            })
          );

          // 6. Dispatch selectedHourlyPeriod to period
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedHourlyPeriod",
              value: payload.params.period
            })
          );
        }
      });
      // Finally set readyToFillMenu to true
      dispatch(
        setProperty({
          type: payload.fileType,
          key: "readyToFillMenu",
          value: true
        })
      );
    });
  };
};

/**
 * Dispatches actions to indicate no results were found for a specific period type
 * @param {Object} dispatch - The dispatch function to dispatch actions
 * @param {Object} payload - The payload containing the file type and period type
 * @param {string} periodType - The period type for which no results were found
 */
function dispatchPeriodDataRetrieved(
  dispatch,
  payload,
  periodType,
  periodData
) {
  // 1. Dispatch periodData dates
  dispatch(
    setPeriodDataKey({
      subObj: "periodData",
      type: payload.fileType,
      period: periodType,
      key: "dates",
      value: periodData
    })
  );

  // 2. Dispatch periodData centers
  dispatch(
    setPeriodDataKey({
      subObj: "periodData",
      type: payload.fileType,
      period: periodType,
      key: "centers",
      value: Object.keys(periodData).sort(function(a, b) {
        return a.toLowerCase().localeCompare(b.toLowerCase());
      })
    })
  );

  // 3. Dispatch selectedSixHPeriod and currentSixHPeriods for six_hour type
  if (periodType === "six_hour") {
    const [selectedSixHPeriod, currentSixHPeriods] = getAvailablePeriods(
      periodData
    );
    // 3.1 Dispatch selectedSixHPeriod
    dispatch(
      setProperty({
        type: payload.fileType,
        key: "selectedSixHPeriod",
        value: selectedSixHPeriod
      })
    );
    // 3.2 Dispatch currentSixHPeriods
    dispatch(
      setProperty({
        type: payload.fileType,
        key: "currentSixHPeriods",
        value: currentSixHPeriods
      })
    );
  } else if (periodType === "hourly") {
    // 4. Dispatch currentHourlyPeriods
    const [selectedHourlyPeriod, currentHourlyPeriods] = getAvailablePeriods(
      periodData
    );
    dispatch(
      setProperty({
        type: payload.fileType,
        key: "currentHourlyPeriods",
        value: currentHourlyPeriods
      })
    );
  }
}

/**
 * Returns the available periods for a specific period type
 * @param {Object} periodData - The period data object
 * @returns {Array} An array of available periods, selectedSixHPeriod and currentSixHPeriods
 */
function getAvailablePeriods(periodData) {
  let firstCenter = Object.keys(periodData).sort(function(a, b) {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  })[0];
  let templateLatestDate = Object.keys(periodData[firstCenter]).sort()[
    Object.keys(periodData[firstCenter]).length - 1
  ];
  let availablePeriods = periodData[firstCenter][templateLatestDate];

  const arrAvailablePeriods = Object.keys(availablePeriods)
    .sort()
    .filter(p => availablePeriods[p].available);
  const selectedPeriod = arrAvailablePeriods[arrAvailablePeriods.length - 1]; // latest available period either six_hour or hourly
  const currentPeriods = availablePeriods; // all available periods either six_hour or hourly
  return [selectedPeriod, currentPeriods];
}

/**
 * Returns the latest hour from the response data
 * @param {Object} response - The response object containing the data
 * @returns {string} The latest hour in the format "HH"
 */
function getLatestHour(response) {
  let dateForHour =
    response.data.results?.[0]?.date ||
    (response.data[0] && response.data[0].date) ||
    new Date().toISOString();
  let latestHour = dateForHour.split("T")[1].split(":")[0];
  return latestHour;
}

/**
 * Dispatches actions to indicate no results were found for a specific period type
 * @param {Object} payload - The payload containing the file type and period type
 * @param {string} periodType - The period type for which no results were found
 * @param {Function} dispatch - The dispatch function to dispatch actions
 */
function dispatchNoResults(payload, periodType, dispatch) {
  dispatch(
    setPeriodDataKey({
      subObj: "periodData",
      type: payload.fileType,
      period: periodType,
      key: "noData",
      value: true
    })
  );
  dispatch(
    setProperty({
      type: payload.fileType,
      key: "dataFetched",
      value: true
    })
  );
}

/**
 * Creates an aggregated view combining data from all centers
 * @param {Object} centersData - Object containing data for each center
 * @returns {Object} Aggregated data structure for 'all' centers view
 */
function createAggregatedCenterView(centersData) {
  const aggregatedData = {};

  // Filter out the 'all' property if it exists to avoid circular references
  const centerKeys = Object.keys(centersData).filter(key => key !== "all");
  console.log("centersData", centersData);
  // Process each center
  for (const center of centerKeys) {
    // Process each date in this center's data
    for (const date of Object.keys(centersData[center])) {
      // Initialize date object if not exists
      if (!aggregatedData[date]) {
        aggregatedData[date] = {};
      }

      // Process each period (hour) for this date
      for (const period of Object.keys(centersData[center][date])) {
        if (!aggregatedData[date][period]) {
          // Initialize period if it doesn't exist yet
          aggregatedData[date][period] = {
            available: false,
            id: {}
          };
        }

        // If this center has data available for this period, mark as available
        // and store the center's ID
        if (centersData[center][date][period].available) {
          aggregatedData[date][period].available = true;
          aggregatedData[date][period].id[center] =
            centersData[center][date][period].id;
        }
      }
    }
  }
  console.log("aggregatedData", aggregatedData);
  return aggregatedData;
}
/**
 * Get Period Availability for hourly and six_hour period
 * @param {Array} responseResults - Raw response data from API
 * @param {String} periodType - Type of period (hourly, six_hour)
 * @returns {Object} Structured data with center/date/hour hierarchy
 */
function organizePeriodAvailabilityHours(responseResults, periodType) {
  // Structure to organize periods by center, date and hour
  const availabilityByCenter = {};

  for (let result of responseResults) {
    if (!availabilityByCenter[result.center]) {
      availabilityByCenter[result.center] = {};
    }

    const dateFormatted = moment.utc(result.date).format("YYYY-MM-DD");
    if (!availabilityByCenter[result.center][dateFormatted]) {
      let hoursObj = {};

      if (periodType === "hourly") {
        // Generate hours 00-23 for hourly periods
        for (let i = 0; i < 24; i++) {
          const hour = i.toString().padStart(2, "0");
          hoursObj[hour] = { available: false, id: null };
        }
      } else if (periodType === "six_hour") {
        // Generate only 00, 06, 12, 18 for six-hour periods
        ["00", "06", "12", "18"].forEach(hour => {
          hoursObj[hour] = { available: false, id: null };
        });
      }

      availabilityByCenter[result.center][dateFormatted] = hoursObj;
    }

    const hourFormatted = moment.utc(result.date).format("HH");
    availabilityByCenter[result.center][dateFormatted][
      hourFormatted
    ].available = true;
    availabilityByCenter[result.center][dateFormatted][hourFormatted].id =
      result.id;
  }

  availabilityByCenter.all = createAggregatedCenterView(availabilityByCenter);

  return availabilityByCenter;
}
/**
 * Get Period Availability for daily period
 * @param {Array} responseResults - Raw response data from API
 * @param {String} periodType - Type of period (daily)
 * @returns {Object} Structured data with center/date hierarchy
 */
function organizePeriodAvailabilityDays(responseResults, periodType) {
  let tempObj = {};
  for (let p of responseResults) {
    if (!tempObj[p.center]) {
      tempObj[p.center] = {};
    }
    tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")] = {
      id: p.id
    };
  }
  // The object that contains the dates for the 'combined' version (all centers)
  let objectAll = {};

  // loop through all centers
  for (let ct of Object.keys(tempObj)) {
    // loop through all dates
    for (let d of Object.keys(tempObj[ct])) {
      // if the date is not in arrayAll, add it
      // if objectAll doesn't have the key 'd'
      if (!Object.keys(objectAll).includes(d)) {
        objectAll[d] = {};
      }
      objectAll[d][ct] = tempObj[ct][d].id;
    }
  }
  tempObj.all = objectAll;
  return tempObj;
}

/**
 * Returns the period availability for a specific period type
 * @param {Array} responseResults - Raw response data from API
 * @param {String} periodType - Type of period (hourly, six_hour, daily)
 * @returns {Object} Structured data with center/date hierarchy
 */
function organizePeriodAvailability(responseResults, periodType) {
  if (periodType === "hourly" || periodType === "six_hour") {
    return organizePeriodAvailabilityHours(responseResults, periodType);
  } else {
    return organizePeriodAvailabilityDays(responseResults, periodType);
  }
}
